import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Users, CheckSquare, UserCheck } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import LoginForm from './components/LoginForm';
import KanbanBoard from './components/KanbanBoard';
import BoardSelector from './components/BoardSelector';
import CreateBoardModal from './components/CreateBoardModal';
import InviteUsersModal from './components/InviteUsersModal';
import InvitationsModal from './components/InvitationsModal';
import PendingInvitationsModal from './components/PendingInvitationsModal';
import ShareBoardModal from './components/ShareBoardModal';

import JoinBoard from './components/JoinBoard';
import ActivityFeed from './components/ActivityFeed';
import BoardSettingsModal from './components/BoardSettingsModal';
import MemberModal from './components/MemberModal';
import Roadmaps from './components/Roadmaps';
import ResourceManagerModal from './components/ResourceManagerModal';
import SatsumaLogo from './components/SatsumaLogo';
import HamburgerMenu from './components/NotificationBell';
import GlobalTaskView from './components/GlobalTaskView';
import { WhatAreWeDoing } from './components/BoardMembers';
import NavigationDropdown from './components/NavigationDropdown';
import UserProfileButton from './components/UserProfileButton';
import UserProfileModal from './components/UserProfileModal';
import { Board, BoardInvitation, Column } from './types';
import { 
  getProducts,
  subscribeToProducts,
  subscribeToColumns,
  getBoardsForUser, 
  createBoard, 
  createInvitation, 
  getInvitationsForUser,
  updateInvitationStatus,
  acceptInvitationViaCallable,
  subscribeToBoards,
  syncBoardMemberIndexViaCallable,
} from './firebase/firestore';

/** Once per tab session per board; retries if callable fails. */
const syncedBoardMemberIndexIds = new Set<string>();
function scheduleBoardMemberIndexSync(boardIds: string[]) {
  for (const id of boardIds) {
    if (syncedBoardMemberIndexIds.has(id)) continue;
    syncedBoardMemberIndexIds.add(id);
    void syncBoardMemberIndexViaCallable(id).catch(() => {
      syncedBoardMemberIndexIds.delete(id);
    });
  }
}

const AppContent: React.FC = () => {
  const { user, isLoading, logout } = useAuth();
  const [boards, setBoards] = useState<Board[]>([]);
  const [currentBoard, setCurrentBoard] = useState<Board | null>(null);
  const [invitations, setInvitations] = useState<BoardInvitation[]>([]);
  const [currentMode, setCurrentMode] = useState<'kanban' | 'standup'>('kanban');
  const [isCreateBoardOpen, setIsCreateBoardOpen] = useState(false);
  const [isInviteUsersOpen, setIsInviteUsersOpen] = useState(false);
  const [isBoardSettingsOpen, setIsBoardSettingsOpen] = useState(false);
  const [isInvitationsOpen, setIsInvitationsOpen] = useState(false);
  const [isPendingInvitationsOpen, setIsPendingInvitationsOpen] = useState(false);
  const [isShareBoardOpen, setIsShareBoardOpen] = useState(false);
  const [isActivityFeedOpen, setIsActivityFeedOpen] = useState(false);
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [isRoadmapsOpen, setIsRoadmapsOpen] = useState(false);
  const [isResourceManagerOpen, setIsResourceManagerOpen] = useState(false);
  const [isUserProfileOpen, setIsUserProfileOpen] = useState(false);
  const [isLoadingBoards, setIsLoadingBoards] = useState(true);
  const [products, setProducts] = useState<any[]>([]);
  const [columns, setColumns] = useState<Column[]>([]);
  const invitationsLoadedRef = useRef(false);

  // Global task view state
  const [isGlobalTaskViewOpen, setIsGlobalTaskViewOpen] = useState(false);

  // Load boards and invitations
  useEffect(() => {
    if (!user) return;

    const loadData = async () => {
      try {
        // Load boards
        const userBoards = await getBoardsForUser(user.id);
        setBoards(userBoards);

        // Repair denormalized memberUserIds when legacy rows used `uid` only (rules + queries need the index).
        scheduleBoardMemberIndexSync(userBoards.map((b) => b.id));
        
        // Set first board as current if none selected.
        // Use functional update so we read the real current state, not the
        // stale closure value captured when this effect mounted (always null).
        if (userBoards.length > 0) {
          setCurrentBoard(prev => prev ?? userBoards[0]);
        }

      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoadingBoards(false);
      }
    };

    loadData();

    // Subscribe to real-time board updates
    const unsubscribe = subscribeToBoards(user.id, (updatedBoards) => {
      setBoards(updatedBoards);
      scheduleBoardMemberIndexSync(updatedBoards.map((b) => b.id));

      // Update current board if it was modified
      if (currentBoard) {
        const updatedCurrentBoard = updatedBoards.find(b => b.id === currentBoard.id);
        if (updatedCurrentBoard) {
          setCurrentBoard(updatedCurrentBoard);
        }
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit currentBoard to avoid resetting selection on board update
  }, [user]);

  // Request notification permissions
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Load invitations separately to prevent infinite loops
  useEffect(() => {
    if (!user || invitationsLoadedRef.current) return;

    const loadInvitations = async () => {
      try {
        console.log('Loading invitations for user:', user.email);
        const userInvitations = await getInvitationsForUser(user.email);
        console.log('Found invitations:', userInvitations.length);
        setInvitations(userInvitations);
        invitationsLoadedRef.current = true;
      } catch (error) {
        console.error('Error loading invitations:', error);
      }
    };

    loadInvitations();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when user email changes, not entire user object
  }, [user?.email]);

  // Load products when current board changes (subscribe for resource_manager so Create Project updates list)
  useEffect(() => {
    if (!user) {
      setProducts([]);
      return;
    }
    if (!currentBoard) {
      setProducts([]);
      return;
    }
    if (currentBoard.boardType === 'resource_manager') {
      const unsub = subscribeToProducts(currentBoard.id, setProducts);
      return () => unsub();
    }
    const loadProducts = async () => {
      try {
        const boardProducts = await getProducts(currentBoard.id);
        setProducts(boardProducts);
      } catch (error) {
        console.error('Error loading products:', error);
      }
    };
    loadProducts();
  }, [currentBoard, user]);

  // Load columns for resource_manager board (for Create Project)
  useEffect(() => {
    if (!user || !currentBoard || currentBoard.boardType !== 'resource_manager') {
      setColumns([]);
      return;
    }
    const unsub = subscribeToColumns(currentBoard.id, setColumns);
    return () => unsub();
  }, [currentBoard, user]);

  const handleCreateBoard = async (boardData: Omit<Board, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!user) return;

    try {
      const boardWithMembers = {
        ...boardData,
        ownerId: user.id,
        members: [{
          userId: user.id,
          email: user.email,
          role: 'owner' as const,
          joinedAt: new Date(),
        }],
      };

      const boardId = await createBoard(boardWithMembers, user.id);
      
      // The board will be loaded via the real-time subscription
      console.log('Board created successfully:', boardId);
    } catch (error) {
      console.error('Error creating board:', error);
    }
  };

  const handleInviteUsers = async (invitationData: Omit<BoardInvitation, 'id' | 'createdAt' | 'expiresAt'>[]) => {
    if (!user || !currentBoard) return;

    try {
      for (const invitation of invitationData) {
        await createInvitation({
          ...invitation,
          email: invitation.email.toLowerCase().trim(), // Normalize email
          boardId: currentBoard.id,
          boardName: currentBoard.name,
          invitedBy: user.id,
          invitedByEmail: user.email.toLowerCase().trim(), // Normalize email
        });
      }
      
      console.log('Invitations sent successfully');
    } catch (error) {
      console.error('Error sending invitations:', error);
    }
  };

  const handleAcceptInvitation = async (invitationId: string) => {
    if (!user) return;
    
    try {
      // Find the invitation to get board and role information
      const invitation = invitations.find(inv => inv.id === invitationId);
      if (!invitation) {
        console.error('Invitation not found');
        return;
      }

      await acceptInvitationViaCallable(invitationId);
      
      // Reload invitations and boards
      const updatedInvitations = await getInvitationsForUser(user.email);
      setInvitations(updatedInvitations);
      
      console.log('Successfully joined board:', invitation.boardName);
    } catch (error) {
      console.error('Error accepting invitation:', error);
    }
  };

  const handleDeclineInvitation = async (invitationId: string) => {
    try {
      await updateInvitationStatus(invitationId, 'declined');
      // Reload invitations
      if (user) {
        const updatedInvitations = await getInvitationsForUser(user.email);
        setInvitations(updatedInvitations);
      }
    } catch (error) {
      console.error('Error declining invitation:', error);
    }
  };

  const handleBoardDeleted = () => {
    // Reset current board and reload boards
    setCurrentBoard(null);
    // The boards will be updated via the subscription
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="glass-effect rounded-3xl p-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <SatsumaLogo size={32} />
            <div className="w-8 h-8 border-4 border-satsuma-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="text-gray-600 text-center">Loading Satsuma...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginForm />;
  }

  if (isLoadingBoards) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-satsuma-100 via-sakura-50 to-matcha-50 flex items-center justify-center">
        <div className="glass-effect rounded-3xl p-8 text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <SatsumaLogo size={32} />
            <div className="w-8 h-8 border-4 border-satsuma-500 border-t-transparent rounded-full animate-spin" />
          </div>
          <p className="text-gray-600">Loading your boards...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Join Board Route */}
      <Route path="/join/:shareLinkId" element={<JoinBoard />} />
      
      {/* Main App Route */}
      <Route path="/*" element={
        <div className="min-h-screen bg-gradient-to-br from-satsuma-100 via-sakura-50 to-matcha-50">
          {/* Header */}
          <header className="glass-effect border-b border-white/30 p-2 sm:p-4 relative z-[9998]">
            <div className="max-w-7xl mx-auto">
              {/* Single row - Logo, board selector, More, and main actions */}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0">
                    <SatsumaLogo size={24} className="w-full h-full" />
                  </div>
                  <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-satsuma-600 to-heian-600 bg-clip-text text-transparent truncate">
                    Satsuma
                  </h1>
                  {invitations.length > 0 && (
                    <div className="relative flex-shrink-0">
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-satsuma-500 rounded-full animate-pulse" />
                      <div className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-red-500 rounded-full" />
                    </div>
                  )}
                  {/* Board selector and More - same row */}
                  {currentBoard && currentBoard.boardType !== 'resource_manager' && (
                    <HamburgerMenu
                      currentMode={currentMode}
                      onModeChange={setCurrentMode}
                      currentBoard={currentBoard}
                    />
                  )}
                  <div className="flex-1 min-w-0 max-w-md">
                    <BoardSelector
                      boards={boards}
                      currentBoard={currentBoard}
                      invitations={invitations}
                      products={products}
                      onBoardSelect={setCurrentBoard}
                      onCreateBoard={() => setIsCreateBoardOpen(true)}
                      onInviteUsers={() => setIsInviteUsersOpen(true)}
                      onBoardSettings={() => setIsBoardSettingsOpen(true)}
                      onViewInvitations={() => setIsInvitationsOpen(true)}
                      onViewPendingInvitations={() => setIsPendingInvitationsOpen(true)}
                      onShareBoard={() => setIsShareBoardOpen(true)}
                    />
                  </div>
                  {currentBoard && (
                    <NavigationDropdown
                      currentBoard={currentBoard}
                      onActivityClick={() => setIsActivityFeedOpen(true)}
                      onRoadmapsClick={() => setIsRoadmapsOpen(true)}
                      onResourceManagerClick={() => setIsResourceManagerOpen(true)}
                    />
                  )}
                </div>

                {/* Right side buttons - wrap on narrow screens so all stay visible */}
                <div className="flex items-center flex-wrap gap-1 sm:gap-2 flex-shrink-0 justify-end">
                  {/* Global Task View Button */}
                  <button
                    onClick={() => setIsGlobalTaskViewOpen(true)}
                    className="relative p-2 sm:p-2.5 text-gray-600 hover:text-satsuma-600 transition-colors touch-manipulation"
                    title="View all my tasks"
                  >
                    <CheckSquare size={18} className="sm:w-5 sm:h-5" />
                  </button>

                  {/* Member Count Button */}
                  {currentBoard && (
                    <button
                      onClick={() => setIsMemberModalOpen(true)}
                      className="relative p-2 sm:p-2.5 text-gray-600 hover:text-satsuma-600 transition-colors touch-manipulation"
                      title={`View board members (${currentBoard.members.length} members)`}
                    >
                      <Users size={18} className="sm:w-5 sm:h-5" />
                    </button>
                  )}

                  {/* Resource Manager Button (when a board is selected, hide when already on Resource Manager board) */}
                  {currentBoard && currentBoard.boardType !== 'resource_manager' && (
                    <button
                      onClick={() => setIsResourceManagerOpen(true)}
                      className="relative flex items-center gap-1.5 p-2 sm:p-2.5 text-gray-600 hover:text-satsuma-600 transition-colors touch-manipulation rounded-lg hover:bg-white/10"
                      title="Resource Manager"
                      aria-label="Resource Manager"
                    >
                      <UserCheck size={18} className="sm:w-5 sm:h-5 flex-shrink-0" />
                      <span className="hidden lg:inline text-sm font-medium">Resources</span>
                    </button>
                  )}

                  {/* User Profile Button */}
                  <UserProfileButton
                    onProfileClick={() => setIsUserProfileOpen(true)}
                    onLogout={logout}
                  />
                </div>
              </div>
            </div>
          </header>

          {/* Main Content */}
          <main className="max-w-7xl mx-auto p-2 sm:p-4 pt-3 sm:pt-6">
            {currentBoard ? (
              currentBoard.boardType === 'resource_manager' ? (
                <ResourceManagerModal
                  isOpen
                  onClose={() => {}}
                  boardId={currentBoard.id}
                  products={products}
                  isStandalone
                  projectsColumnId={columns[0]?.id}
                  boardName={currentBoard.name}
                />
              ) : currentMode === 'kanban' ? (
                <KanbanBoard 
                  boardId={currentBoard.id} 
                  boardType={currentBoard.boardType}
                  scoringConfig={currentBoard.priorityScoringConfig}
                  boardMembers={currentBoard.members}
                />
              ) : (
                <WhatAreWeDoing 
                  boardId={currentBoard.id}
                  boardMembers={currentBoard.members}
                />
              )
            ) : (
              <div className="text-center py-12">
                <div className="glass-effect rounded-3xl p-8 max-w-md mx-auto">
                  <div className="flex items-center justify-center space-x-3 mb-4">
                    <SatsumaLogo size={48} />
                    <h2 className="text-xl font-semibold text-gray-800">Welcome to Satsuma!</h2>
                  </div>
                  <p className="text-gray-600 mb-6">Create your first board to start tracking product ideas.</p>
                  <button
                    onClick={() => setIsCreateBoardOpen(true)}
                    className="btn-primary"
                  >
                    Create Your First Board
                  </button>
                </div>
              </div>
            )}
          </main>

          {/* Modals */}
          <CreateBoardModal
            isOpen={isCreateBoardOpen}
            onClose={() => setIsCreateBoardOpen(false)}
            onCreateBoard={handleCreateBoard}
          />

          <InviteUsersModal
            isOpen={isInviteUsersOpen}
            onClose={() => setIsInviteUsersOpen(false)}
            onInviteUsers={handleInviteUsers}
            boardName={currentBoard?.name || ''}
            boardId={currentBoard?.id || ''}
          />

          <PendingInvitationsModal
            isOpen={isPendingInvitationsOpen}
            onClose={() => setIsPendingInvitationsOpen(false)}
            boardId={currentBoard?.id || ''}
            boardName={currentBoard?.name || ''}
          />

          <InvitationsModal
            isOpen={isInvitationsOpen}
            onClose={() => setIsInvitationsOpen(false)}
            invitations={invitations}
            onAcceptInvitation={handleAcceptInvitation}
            onDeclineInvitation={handleDeclineInvitation}
          />

          <ShareBoardModal
            isOpen={isShareBoardOpen}
            onClose={() => setIsShareBoardOpen(false)}
            boardId={currentBoard?.id || ''}
            boardName={currentBoard?.name || ''}
            currentUserEmail={user?.email || ''}
            currentUserId={user?.id || ''}
          />

          {currentBoard && (
            <BoardSettingsModal
              isOpen={isBoardSettingsOpen}
              onClose={() => setIsBoardSettingsOpen(false)}
              board={currentBoard}
              onBoardDeleted={handleBoardDeleted}
            />
          )}

          {/* Activity Feed Modal */}
          {currentBoard && (
            <ActivityFeed
              boardId={currentBoard.id}
              isOpen={isActivityFeedOpen}
              onClose={() => setIsActivityFeedOpen(false)}
            />
          )}

          {/* Member Modal */}
          {currentBoard && (
            <MemberModal
              isOpen={isMemberModalOpen}
              onClose={() => setIsMemberModalOpen(false)}
              board={currentBoard}
            />
          )}

          {/* Roadmaps Modal */}
          {currentBoard && (
            <Roadmaps
              boardId={currentBoard.id}
              isOpen={isRoadmapsOpen}
              onClose={() => setIsRoadmapsOpen(false)}
            />
          )}

          {/* Resource Manager Modal - only when not already on a Resource Manager board */}
          {currentBoard && currentBoard.boardType !== 'resource_manager' && (
            <ResourceManagerModal
              isOpen={isResourceManagerOpen}
              onClose={() => setIsResourceManagerOpen(false)}
              boardId={currentBoard.id}
              products={products}
            />
          )}

          {/* User Profile Modal */}
          <UserProfileModal
            isOpen={isUserProfileOpen}
            onClose={() => setIsUserProfileOpen(false)}
          />

          {/* Global Task View */}
          <GlobalTaskView
            isOpen={isGlobalTaskViewOpen}
            onClose={() => setIsGlobalTaskViewOpen(false)}
            boardMembers={currentBoard?.members || []}
          />

          {/* Legacy Invitations Panel - Keep for now but will be removed */}
          {invitations.length > 0 && (
            <div className="fixed bottom-4 right-4 z-40">
              <div className="glass-effect rounded-2xl p-4 max-w-sm">
                <h3 className="font-semibold text-gray-800 mb-2">Pending Invitations</h3>
                <div className="space-y-2">
                  {invitations.map((invitation) => (
                                          <div key={invitation.id} className="flex items-center justify-between p-2 bg-white/15 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800">{invitation.email}</p>
                        <p className="text-xs text-gray-600">{invitation.role}</p>
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleAcceptInvitation(invitation.id)}
                          className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                        >
                          Accept
                        </button>
                        <button
                          onClick={() => handleDeclineInvitation(invitation.id)}
                          className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      } />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <NotificationProvider>
        <AppContent />
      </NotificationProvider>
    </AuthProvider>
  );
};

export default App;
